import { ActionKind, OrderShipmentStatus, type GetOrderQuery } from '@codezo/graphql';
import { ActionBox, ActionContext, StepDot, type FlowRowState } from '@codezo/order-detail';
import { ExternalLink, Truck } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { UpdateShipmentTrackingModal } from '../../../components/orders/UpdateShipmentTrackingModal';
import { formatDate } from '../../../lib/utils';

/**
 * Where a box has got to, in the stepper's own vocabulary.
 *
 * The boxes all live under Shipping rather than moving to the step they reached,
 * because a box has four states and the stepper has two: PENDING belongs under
 * neither "Shipping" nor "Delivered", and splitting the list would stop
 * "Shipment 1" and "Shipment 2" being adjacent — which is the one thing their
 * numbering is for.
 *
 * So the stage is said on the box instead. Same ring the steps above use: a
 * delivered box reads as done where it sits, rather than as another line under
 * a heading that says "Shipping".
 */
const SHIPMENT_STATE: Record<OrderShipmentStatus, FlowRowState> = {
	[OrderShipmentStatus.Pending]: 'upcoming',
	[OrderShipmentStatus.Shipped]: 'active',
	[OrderShipmentStatus.Delivered]: 'done',
	[OrderShipmentStatus.Cancelled]: 'skipped',
};

type Order = NonNullable<GetOrderQuery['getOrder']>;
type OrderItem = Order['items'][number];
/** A box carrying this item, with how much of it travels in that box. */
type ItemShipment = NonNullable<OrderItem['shipments']>[number];

/**
 * The boxes carrying this item. A split product is on more than one.
 *
 * Served by the backend as `OrderItem.shipments`, already in the order they go
 * out and already carrying the per-product quantity. This used to filter
 * `order.shipments` on their lines and then re-reduce the quantity further down
 * — the same answer, derived twice, from a relationship that only ran
 * shipment-to-item.
 *
 * Reading the item's own field also fixes the case the derivation could not see:
 * under a combination plan the item's flat tracking columns are null, because
 * shipping there belongs to the plan's shipments rather than to the product.
 */
export function shipmentsFor(item: OrderItem): ItemShipment[] {
	return item.shipments ?? [];
}

/* `isShipmentMove` and `openShipmentActor` lived here. A shipment's actions
 * include EDIT_DETAILS, which is never blocked, so reading ownership off them
 * put "Your action" on every box that had ever existed. `OrderItem.waitingOn`
 * answers ownership now, and `kind` says what an action is. */

/**
 * A shipment's own moves, ranked and rendered.
 *
 * `kind` does the ranking, and it is the only thing that can: at SHIPPED two
 * actions come back together and they are not peers. DELIVER is a MOVE — the
 * step the box is actually waiting on. EDIT_DETAILS is a PROPERTY — it corrects
 * a carrier or a date and moves nothing. Two equal buttons would make a
 * one-step flow look like a fork, so a property drops to ghost.
 *
 * CANCEL is filtered by the caller where it does not belong, not here.
 *
 * A blocked action is shown DISABLED with its reason rather than hidden. Today
 * only SHIP is ever blocked — goods that have not been made cannot leave — and
 * the admin needs to see why the button will not move, which a missing button
 * cannot tell them.
 */
export function ShipmentActionButtons({
	actions,
	onPick,
}: {
	actions: ItemShipment['shipment']['availableActions'];
	onPick: (actionKey: string) => void;
}) {
	return (
		<>
			{(actions ?? []).map((a) => (
				<Button
					key={a.key}
					size="xs"
					variant={a.kind === ActionKind.Move ? 'outline' : 'ghost'}
					disabled={!!a.blocked}
					title={a.blocked ? (a.blockedReason ?? undefined) : undefined}
					onClick={() => onPick(a.key)}
				>
					{a.label}
				</Button>
			))}
		</>
	);
}

/**
 * What the studio can do about getting this item to the client.
 *
 * ── Why the Shipping row was dead ──
 *
 * The spine read its moves from `item.availableActions`, and shipping is not on
 * the item. Goods go in a BOX: the order carries a RECORD_SHIPMENT action that
 * creates one, and from then on every move — mark it shipped, mark it delivered,
 * correct the tracking — belongs to `OrderShipment.availableActions`. So an item
 * sitting at Shipping offered nothing, on either product, with no way forward
 * from the flow at all.
 *
 * This is the same shape as the sample row, and the same fix: the row the
 * product is standing on renders the moves of the THING that row is about. The
 * sample row reads `samples[].availableActions`; this reads the shipments'.
 *
 * ── Two states, both of them real ──
 *
 * Not boxed yet: the only move is the order's own RECORD_SHIPMENT, which is
 * where the header button goes too. It arrives blocked with a reason while
 * nothing has finished production or everything produced is already on a box —
 * so the row can say what is holding it without knowing the rule.
 *
 * Boxed: one strip per box carrying this item, each with its own carrier,
 * tracking and moves. A split product legitimately has two, in different states,
 * and collapsing them would be the same lie the progress matrix avoids.
 *
 * CANCEL is filtered out, like every other escape in this spine: it undoes the
 * leg rather than advancing it, and it already lives on the shipments section.
 */
export function ShipmentActions({
	item,
	recordShipment,
	onChanged,
}: {
	item: OrderItem;
	/**
	 * The order-level RECORD_SHIPMENT action and the page's own opener. Passed
	 * in rather than read here: one modal for the whole order, owned by the page,
	 * the same one the header button opens.
	 */
	recordShipment?: {
		label: string;
		blocked?: boolean | null;
		blockedReason?: string | null;
		onOpen: () => void;
	};
	onChanged: () => void;
}) {
	// The BOX being corrected, as the item's own list hands it over. Typed off
	// that list rather than the order's: the modal takes a structural
	// `ShipmentForTracking`, so the narrower selection satisfies it and the two
	// lists no longer have to agree field for field.
	const [editing, setEditing] = useState<{
		shipment: ItemShipment['shipment'];
		actionKey: string;
	} | null>(null);

	const shipments = shipmentsFor(item);

	// Nothing carrying it yet — the move is to put it in a box.
	if (shipments.length === 0) {
		if (!recordShipment) return null;
		return (
			<ActionBox mine={!recordShipment.blocked}>
				<ActionContext>
					{recordShipment.blocked
						? (recordShipment.blockedReason ?? 'Not ready to ship yet')
						: 'Not on a shipment yet.'}
				</ActionContext>
				{!recordShipment.blocked && (
					<Button size="xs" variant="outline" onClick={recordShipment.onOpen}>
						{recordShipment.label}
					</Button>
				)}
			</ActionBox>
		);
	}

	return (
		<>
			{shipments.map(({ shipment, quantity }) => {
				const actions = (shipment.availableActions ?? []).filter((a) => a.key !== 'CANCEL');
				const blocked = actions.find((a) => a.blocked && a.blockedReason);

				// What this box is and where it has got to. The quantity matters on a
				// split product: "Shipment 2 · 100 of 250" is the whole reason there
				// are two strips here. It comes off the entry now — the server says
				// how much of THIS product is in the box, rather than the page
				// re-summing the box's lines to work it out.
				const tracking = [shipment.carrier, shipment.trackingNumber]
					.filter(Boolean)
					.join(' ');
				const when = shipment.actualDeliveryDate
					? `delivered ${formatDate(shipment.actualDeliveryDate)}`
					: shipment.shippedAt
						? `shipped ${formatDate(shipment.shippedAt)}`
						: shipment.estimatedDeliveryDate
							? `est. ${formatDate(shipment.estimatedDeliveryDate)}`
							: null;

				return (
					<div key={shipment.id} className="mt-2 flex gap-2.5">
						{/* 12px, matching the sample stepper's sub-step rail, so a box
						    and a step read at the same rank. */}
						<span className="mt-[3px] flex w-[12px] shrink-0 justify-center">
							<StepDot size={12} state={SHIPMENT_STATE[shipment.status]} muted />
						</span>
						<div className="min-w-0 flex-1">
							{/* The sample stepper's grammar, because the two sit one above
						    the other on this page: the MOVE on the row that names the
						    thing, and tracking on a quiet line of its own beneath with
						    the truck and its own Track.
						    This used to be one strip holding the box, its tracking and
						    both buttons — which read as a different kind of thing from
						    the sample directly below, for no reason other than how it
						    had been built. */}
							<div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
								<span className="min-w-0 flex-1 text-xs text-text-2">
									{`Shipment ${shipment.sequence}`}
									{quantity > 0 && quantity < item.quantity
										? ` · ${quantity.toLocaleString()} of ${item.quantity.toLocaleString()}`
										: ''}
									{when ? ` · ${when}` : ''}
								</span>
								{/* A MOVE and a PROPERTY are not peers: `kind` says which is
							    which, so the move keeps the outline and a property —
							    "Edit details", which changes a field and moves nothing —
							    drops to ghost. */}
								<ShipmentActionButtons
									actions={actions}
									onPick={(actionKey) => setEditing({ shipment, actionKey })}
								/>
							</div>

							{blocked && (
								<div className="mt-1 text-2xs text-text-4">
									{blocked.blockedReason}
								</div>
							)}

							{tracking && (
								<div className="mt-1.5 flex flex-wrap items-center gap-2">
									<Truck size={13} className="shrink-0 text-text-3" aria-hidden />
									<span className="font-mono text-xs text-text-4">
										{tracking}
									</span>
									{/* Only where the server resolved somewhere real to go. */}
									{shipment.trackingUrl && (
										<Button
											size="xs"
											variant="outline"
											onClick={() =>
												window.open(
													shipment.trackingUrl!,
													'_blank',
													'noopener'
												)
											}
										>
											Track
											<ExternalLink size={12} data-icon="inline-end" />
										</Button>
									)}
								</div>
							)}
						</div>
					</div>
				);
			})}

			{editing && (
				<UpdateShipmentTrackingModal
					shipment={editing.shipment}
					initialActionKey={editing.actionKey}
					onClose={() => setEditing(null)}
					onSaved={() => {
						setEditing(null);
						onChanged();
					}}
				/>
			)}
		</>
	);
}
